// Toast Town Speedway — top-down racing sim + HUD + results.
// The track is one centreline spline; everything (mesh, AI line, on/off-track,
// lap progress, position) is derived from it.

import * as THREE from 'three';
import { recordRace } from '../core/save.js';

const LAPS = 3;
const TRACK_WIDTH = 14;
const GRASS_SPEED_MULT = 0.60;
const GRASS_GRIP_MULT = 0.55;
const LOOKAHEAD = 7;
const AI_RUBBER_BAND = 12;   // units behind/ahead before grip/topSpeed nudge
const PAYOUT = [40, 25, 12, 5];
const BEST_LAP_BONUS = 5;

export class TrackSpline {
  constructor(points){
    // points: array of THREE.Vector3 centreline knots forming a closed loop
    this.points = points.map(p => p.clone());
    this.closed = true;
    this._buildLengths();
  }

  _buildLengths(){
    this.lengths = [0];
    this.total = 0;
    for (let i = 1; i < this.points.length; i++){
      this.total += this.points[i-1].distanceTo(this.points[i]);
      this.lengths.push(this.total);
    }
    if (this.closed){
      this.total += this.points[this.points.length-1].distanceTo(this.points[0]);
    }
  }

  // map s [0,1] to point on the loop
  getPoint(s){
    s = ((s % 1) + 1) % 1;
    const dist = s * this.total;
    let i = 1;
    while (i < this.lengths.length && this.lengths[i] < dist) i++;
    const prev = this.points[i-1];
    const curr = this.points[i % this.points.length];
    const prevLen = this.lengths[i-1];
    const segLen = prev.distanceTo(curr);
    const t = segLen > 0.001 ? Math.min(1, Math.max(0, (dist - prevLen) / segLen)) : 0;
    return new THREE.Vector3().lerpVectors(prev, curr, t);
  }

  getTangent(s){
    const eps = 0.005;
    const a = this.getPoint(s - eps);
    const b = this.getPoint(s + eps);
    return new THREE.Vector3().subVectors(b, a).normalize();
  }

  // project a world position to nearest (s, distance, point) on the centreline
  project(pos){
    let bestS = 0, bestD = Infinity, bestP = this.points[0];
    const n = this.points.length;
    for (let i = 0; i < n; i++){
      const a = this.points[i];
      const b = this.points[(i + 1) % n];
      const ab = new THREE.Vector3().subVectors(b, a);
      const len2 = ab.lengthSq();
      let t = 0;
      if (len2 > 0.0001){
        const ap = new THREE.Vector3().subVectors(pos, a);
        t = Math.max(0, Math.min(1, ap.dot(ab) / len2));
      }
      const p = new THREE.Vector3().lerpVectors(a, b, t);
      const d = p.distanceTo(pos);
      if (d < bestD){ bestD = d; bestP = p; bestS = (this.lengths[i] + Math.sqrt(len2) * t) / this.total; }
    }
    return { s: bestS, distance: bestD, point: bestP };
  }

  // curvature at s, approximated from angle change over a short step
  curvature(s){
    const eps = 0.04;
    const t1 = this.getTangent(s - eps);
    const t2 = this.getTangent(s + eps);
    return Math.acos(Math.max(-1, Math.min(1, t1.dot(t2)))) / (eps * 2);
  }
}

export class Race {
  constructor({ spline, cars, input, onFinish } = {}){
    this.spline = spline;
    this.cars = cars;          // [{ id, name, color, isPlayer, stats, driverNoise }]
    this.input = input;
    this.onFinish = onFinish || (() => {});
    this.active = false;
    this.finished = false;

    this.startTime = 0;
    this.elapsed = 0;
    this.bestLap = null;

    this.checkpoints = this._makeCheckpoints();
    this.state = this.cars.map((c, i) => {
      const s = c.startS || 0;
      return {
        id: c.id,
        isPlayer: c.isPlayer,
        name: c.name,
        color: c.color,
        pos: this.spline.getPoint(s),
        heading: Math.atan2(this.spline.getTangent(s).x, this.spline.getTangent(s).z),
        velocity: 0,
        s,
        lap: 0,
        nextCp: 1,
        finished: false,
        finishTime: null,
        lapStart: 0,
        lastLapMs: null,
        stats: c.stats,
        driverNoise: c.driverNoise || {},
        aiOffset: (Math.random() - 0.5) * 0.06,
        wobble: Math.random() * Math.PI * 2,
        gridIndex: i
      };
    });

    this._onKey = this._onKey.bind(this);
    this._touch = { left: false, right: false, brake: false };
  }

  _makeCheckpoints(){
    const cps = [];
    const n = 8; // checkpoints around the loop
    for (let i = 0; i < n; i++){
      const s = i / n;
      cps.push({ s, point: this.spline.getPoint(s) });
    }
    return cps;
  }

  mount(root){
    const el = document.createElement('div');
    el.className = 'race-hud';
    el.innerHTML = `
      <div class="race-info">
        <span class="pill race-lap">lap 1/${LAPS}</span>
        <span class="pill race-pos">4th</span>
        <span class="pill race-timer">0:00.00</span>
      </div>
      <div class="race-hint">← A / D → steer · SPACE brake</div>
      <div class="race-touch">
        <div class="race-touch-left"></div>
        <div class="race-touch-right"></div>
      </div>
      <button class="race-brake">BRAKE</button>
      <button class="cafe-quit race-quit">✕ quit</button>`;
    (root || document.body).appendChild(el);
    this.el = el;
    this.lapEl = el.querySelector('.race-lap');
    this.posEl = el.querySelector('.race-pos');
    this.timerEl = el.querySelector('.race-timer');

    const left = el.querySelector('.race-touch-left');
    const right = el.querySelector('.race-touch-right');
    const brake = el.querySelector('.race-brake');
    const quit = el.querySelector('.race-quit');

    const bind = (target, key) => {
      target.addEventListener('pointerdown', e => { e.preventDefault(); this._touch[key] = true; });
      target.addEventListener('pointerup', e => { e.preventDefault(); this._touch[key] = false; });
      target.addEventListener('pointerleave', e => { this._touch[key] = false; });
    };
    bind(left, 'left');
    bind(right, 'right');
    bind(brake, 'brake');

    quit.addEventListener('click', () => this._finish());
    window.addEventListener('keydown', this._onKey);
  }

  _onKey(e){
    if (!this.active) return;
    if (e.code === 'Escape'){ e.preventDefault(); this._finish(); }
  }

  begin(){
    this.active = true;
    this.startTime = 0;
    this.state.forEach(s => { s.lapStart = 0; });
  }

  get player(){ return this.state.find(s => s.isPlayer); }

  update(dt){
    if (!this.active || this.finished) return;
    this.elapsed += dt;

    for (const s of this.state){
      if (s.finished) continue;
      this._updateCar(s, dt);
    }
    this._renderHUD();

    const unfinished = this.state.filter(s => !s.finished);
    if (unfinished.length === 0) this._finish();
  }

  _updateCar(s, dt){
    const stats = s.stats;
    let steer = 0;
    let braking = false;

    if (s.isPlayer){
      const k = this.input;
      steer = (k.steer || 0);
      if (this._touch.left) steer = -1;
      if (this._touch.right) steer = 1;
      braking = !!(k.brake || this._touch.brake);
    } else {
      const ai = this._aiSteer(s, dt);
      steer = ai.steer;
      braking = ai.brake;
    }

    // off-track / grass penalties
    const proj = this.spline.project(s.pos);
    const onTrack = proj.distance <= TRACK_WIDTH / 2;
    const speedCap = stats.topSpeed * (onTrack ? 1 : GRASS_SPEED_MULT);
    const grip = stats.grip * (onTrack ? 1 : GRASS_GRIP_MULT);

    // auto-throttle
    if (braking){
      s.velocity -= (stats.accel * 1.6 + 8) * dt;
    } else {
      s.velocity += stats.accel * dt;
    }
    s.velocity = Math.max(0, Math.min(s.velocity, speedCap));

    // steering
    const steerRate = grip * 2.4;
    s.heading += steer * steerRate * dt;

    // mild AI rubber-banding against the player
    if (!s.isPlayer){
      const player = this.player;
      if (player){
        const lead = this._gap(s, player);
        if (lead > AI_RUBBER_BAND) s.velocity *= 1.008;
        else if (lead < -AI_RUBBER_BAND) s.velocity *= 0.995;
      }
    }

    // move
    const dir = new THREE.Vector3(Math.sin(s.heading), 0, Math.cos(s.heading));
    s.pos.addScaledVector(dir, s.velocity * dt);

    // update spline progress
    const next = this.spline.project(s.pos);
    s.s = next.s;

    // checkpoints + laps
    const cp = this.checkpoints[s.nextCp];
    if (cp.point.distanceTo(s.pos) < 5){
      s.nextCp++;
      if (s.nextCp >= this.checkpoints.length){
        s.nextCp = 0;
        s.lap++;
        const lapMs = Math.floor((this.elapsed - s.lapStart) * 1000);
        s.lastLapMs = lapMs;
        if (s.isPlayer && (this.bestLap == null || lapMs < this.bestLap)) this.bestLap = lapMs;
        s.lapStart = this.elapsed;
        if (s.lap >= LAPS){
          s.finished = true;
          s.finishTime = this.elapsed;
        }
      }
    }
  }

  _aiSteer(s, dt){
    // target a point lookahead metres ahead on the racing line, with per-driver noise
    const aheadS = (s.s + LOOKAHEAD / this.spline.total + s.aiOffset) % 1;
    const target = this.spline.getPoint(aheadS);
    const dx = target.x - s.pos.x;
    const dz = target.z - s.pos.z;
    let desired = Math.atan2(dx, dz);

    // wobble
    s.wobble += dt * (s.driverNoise.wobbleSpeed || 2);
    desired += Math.sin(s.wobble) * (s.driverNoise.wobble || 0.08);

    let diff = desired - s.heading;
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    const steer = Math.max(-1, Math.min(1, diff * 1.8));

    // brake for high curvature or tight approach, but never stand still
    const curv = this.spline.curvature((s.s + 0.03) % 1);
    const brake = s.velocity > 8 && (curv > (s.driverNoise.brave || 0.35) || Math.abs(steer) > 0.85);
    return { steer, brake };
  }

  _gap(ahead, behind){
    // signed distance ahead is behind on the track (approximate using s)
    let ds = ahead.s - behind.s;
    if (ds < -0.5) ds += 1;
    if (ds > 0.5) ds -= 1;
    return ds * this.spline.total + (ahead.lap - behind.lap) * this.spline.total;
  }

  _order(){
    return [...this.state].sort((a, b) => {
      if (b.lap !== a.lap) return b.lap - a.lap;
      return b.s - a.s;
    });
  }

  _placement(s){
    const order = this._order();
    const idx = order.indexOf(s);
    return idx + 1;
  }

  _renderHUD(){
    const p = this.player;
    if (!p) return;
    this.lapEl.textContent = `lap ${Math.min(p.lap + 1, LAPS)}/${LAPS}`;
    const place = this._placement(p);
    const suffix = ['th','st','nd','rd'][place] || 'th';
    this.posEl.textContent = `${place}${suffix}`;
    this.timerEl.textContent = this._formatMs(this.elapsed * 1000);
  }

  _formatMs(ms){
    const totalSec = Math.floor(ms / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    const cs = Math.floor((ms % 1000) / 10);
    return `${m}:${String(s).padStart(2,'0')}.${String(cs).padStart(2,'0')}`;
  }

  _finish(){
    if (this.finished) return;
    this.finished = true;
    this.active = false;

    // ensure any car that hasn't crossed the line gets a rank by current position
    const order = this._order();
    const player = this.player;
    const placement = order.indexOf(player) + 1;
    const crumbs = PAYOUT[Math.min(placement - 1, PAYOUT.length - 1)] || 0;
    const best = this.bestLap;
    const bonus = (best && best === this.bestLap && best > 0) ? BEST_LAP_BONUS : 0;

    recordRace(placement, best);

    this.onFinish({
      placement,
      totalMs: Math.floor(this.elapsed * 1000),
      bestLapMs: best,
      crumbs: crumbs + bonus,
      bonus: bonus > 0
    });
  }

  unmount(){
    window.removeEventListener('keydown', this._onKey);
    if (this.el){ this.el.remove(); this.el = null; }
  }
}
